import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, Send, Mic, Square, Upload } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import WeeklySummaries from "@/pages/WeeklySummaries";

export default function Summaries() {
  const { user } = useAuth();
  const canSend = ["ADMIN", "MANAGER"].includes(user?.role);
  const canGen = ["ADMIN", "MANAGER", "COORDINATOR"].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [summaries, setSummaries] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sendItem, setSendItem] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [editText, setEditText] = useState("");
  const [recordingId, setRecordingId] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [regenerating, setRegenerating] = useState(null);

  const load = () => {
    const q = projectId ? `?project_id=${projectId}` : "";
    api.get(`/summaries${q}`).then((r) => setSummaries(r.data));
  };
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)); }, []);
  useEffect(() => { load(); }, [projectId]);

  const generate = async () => {
    if (!projectId) { toast.error("Select a project"); return; }
    setGenerating(true);
    try {
      await api.post(`/summaries/generate?project_id=${projectId}&date=${date}`);
      toast.success("Summary generated");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };

  const saveEdit = async (s) => {
    try {
      await api.put(`/summaries/${s.id}`, { edited_client_text: editText });
      toast.success("Client note saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const startRecording = async (summaryId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        regenerateWithAudio(summaryId, new File([blob], "note.webm", { type: "audio/webm" }));
      };
      recorder.start();
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      setRecordingId(summaryId);
    } catch (e) { toast.error("Microphone access denied or unavailable"); }
  };

  const stopRecording = () => {
    mediaRecorder?.stop();
    setMediaRecorder(null);
    setRecordingId(null);
  };

  const handleFileUpload = (summaryId, e) => {
    const file = e.target.files?.[0];
    if (file) regenerateWithAudio(summaryId, file);
    e.target.value = "";
  };

  const regenerateWithAudio = async (summaryId, file) => {
    setRegenerating(summaryId);
    try {
      const form = new FormData();
      form.append("audio_file", file);
      await api.post(`/summaries/${summaryId}/regenerate-with-audio`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Client summary regenerated from your note");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRegenerating(null); }
  };

  const doSend = async () => {
    try {
      await api.post(`/summaries/${sendItem.id}/send`, { recipient_email: recipient });
      toast.success(`Email sent to ${recipient}`);
      setSendItem(null); setRecipient("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Summaries</h1>
        <p className="text-muted-foreground mt-1">Generate, review and send daily and weekly project updates.</p>
      </div>

      <Tabs defaultValue="daily">
        <TabsList>
          <TabsTrigger value="daily" data-testid="summaries-tab-daily">Daily</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="summaries-tab-weekly">Weekly</TabsTrigger>
        </TabsList>
        <TabsContent value="daily" className="space-y-6 mt-4">

      {canGen && (
        <Card><CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1 flex-1 min-w-[180px]">
            <Label>Project</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="summary-project-select"><SelectValue placeholder="All projects" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="summary-date-input" />
          </div>
          <Button onClick={generate} disabled={generating || !projectId} className="rounded-full" data-testid="generate-summary-button">
            <Sparkles className="h-4 w-4 mr-1" /> {generating ? "Generating…" : "Generate"}
          </Button>
        </CardContent></Card>
      )}

      <div className="space-y-3">
        {summaries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No summaries yet. Generate one above.</p>
        ) : summaries.map((s) => (
          <Card key={s.id} data-testid={`summary-${s.id}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex flex-wrap justify-between items-start gap-2">
                <div>
                  <div className="font-heading font-semibold">{s.project?.name}</div>
                  <div className="text-xs font-mono text-muted-foreground">{s.project?.code} · {s.summary_date} · {s.message_count} msgs</div>
                </div>
                <StatusBadge status={s.status} />
              </div>
              {s.status === "FAILED" ? (
                <p className="text-sm text-destructive">{s.generation_error}</p>
              ) : (
                <>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Internal Brief</div>
                    <p className="text-sm whitespace-pre-wrap">{s.internal_text || "—"}</p>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Client-safe Note</div>
                    {canSend ? (
                      <Textarea rows={3} defaultValue={s.edited_client_text || s.client_text}
                        onFocus={() => setEditText(s.edited_client_text || s.client_text)}
                        onChange={(e) => setEditText(e.target.value)}
                        onBlur={() => (editText && editText !== (s.edited_client_text || s.client_text)) && saveEdit(s)}
                        data-testid={`client-text-${s.id}`} />
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{s.edited_client_text || s.client_text || "—"}</p>
                    )}
                  </div>
                  {canSend && s.status !== "NO_ACTIVITY" && (
                    <div className="flex flex-wrap items-center gap-2">
                      {recordingId === s.id ? (
                        <Button size="sm" variant="destructive" className="rounded-full" onClick={stopRecording} data-testid={`stop-recording-${s.id}`}>
                          <Square className="h-4 w-4 mr-1" /> Stop
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-full" disabled={regenerating === s.id}
                          onClick={() => startRecording(s.id)} data-testid={`record-audio-${s.id}`}>
                          <Mic className="h-4 w-4 mr-1" /> Record note
                        </Button>
                      )}
                      <label className="inline-flex">
                        <input type="file" accept="audio/*" className="hidden" id={`audio-upload-${s.id}`}
                          onChange={(e) => handleFileUpload(s.id, e)} data-testid={`upload-audio-${s.id}`} />
                        <Button size="sm" variant="outline" className="rounded-full" disabled={regenerating === s.id} asChild>
                          <span onClick={() => document.getElementById(`audio-upload-${s.id}`).click()}>
                            <Upload className="h-4 w-4 mr-1" /> Upload note
                          </span>
                        </Button>
                      </label>
                      {regenerating === s.id && <span className="text-xs text-muted-foreground">Transcribing & regenerating…</span>}
                    </div>
                  )}
                  {canSend && s.status !== "NO_ACTIVITY" && (
                    <Button size="sm" className="rounded-full"
                      onClick={() => { setSendItem(s); setRecipient(s.project?.client?.contact_email || ""); }}
                      data-testid={`send-summary-${s.id}`}>
                      <Send className="h-4 w-4 mr-1" /> Send to Client
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!sendItem} onOpenChange={(o) => !o && setSendItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send client summary</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">The client-safe note is emailed to this address.</p>
          <div className="space-y-1">
            <Label>Recipient Email</Label>
            <Input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="client@example.com" data-testid="send-recipient-input" />
          </div>
          <DialogFooter>
            <Button onClick={doSend} disabled={!recipient} data-testid="confirm-send-button">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
        <TabsContent value="weekly" className="mt-4">
          <WeeklySummaries />
        </TabsContent>
      </Tabs>
    </div>
  );
}
